import { useEffect } from "react";

import type { ScrubDirection } from "@/lib/timeline/playback";
import {
  shouldTimelineSpaceTogglePlay,
  timelineArrowScrubDirection,
} from "@/lib/timeline/view/TimelineKeyboardShortcuts/shortcuts";

export function useTimelinePlaybackShortcuts(
  togglePlay: () => void,
  setScrub: (direction: ScrubDirection) => void,
): void {
  useEffect(() => {
    // Holding an arrow works like holding the rewind or fast forward button.
    let heldKey: string | null = null;
    const onArrowDown = (event: KeyboardEvent): void => {
      const direction = timelineArrowScrubDirection(event);
      if (direction === null) {
        return;
      }
      event.preventDefault();
      if (!event.repeat) {
        heldKey = event.key;
        setScrub(direction);
      }
    };
    const onArrowUp = (event: KeyboardEvent): void => {
      if (event.key === heldKey) {
        heldKey = null;
        setScrub(0);
      }
    };
    const onLeave = (): void => {
      if (heldKey !== null) {
        heldKey = null;
        setScrub(0);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.code !== "Space" && event.key !== " ") {
        return;
      }
      if (event.repeat) {
        return;
      }
      if (!shouldTimelineSpaceTogglePlay(event.target)) {
        return;
      }
      event.preventDefault();
      togglePlay();
    };
    // A clicked control keeps focus, which would give it the next Space press.
    // A Space or Enter press reports detail 0, so keyboard users keep their place.
    const releaseClicked = (event: MouseEvent): void => {
      if (event.detail > 0 && document.activeElement instanceof HTMLButtonElement) {
        document.activeElement.blur();
      }
    };
    const releaseSelect = (event: Event): void => {
      if (event.target instanceof HTMLSelectElement) {
        event.target.blur();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keydown", onArrowDown);
    window.addEventListener("keyup", onArrowUp);
    window.addEventListener("blur", onLeave);
    window.addEventListener("click", releaseClicked);
    window.addEventListener("change", releaseSelect);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keydown", onArrowDown);
      window.removeEventListener("keyup", onArrowUp);
      window.removeEventListener("blur", onLeave);
      window.removeEventListener("click", releaseClicked);
      window.removeEventListener("change", releaseSelect);
    };
  }, [setScrub, togglePlay]);
}
